import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { ApiError, captureRecapBoundary, feedbackConversationAuthorityFingerprint, getRecap, getRecapAcknowledgments,
  getRecapOwnReading, isCurrentRecapScope, isRecapContentUnavailable, observeAuthTokenChanges,
  prepareRecapOwnRead, serveRecapFullView } from '@/lib/api';
import { captureRecapCommand } from '@/lib/recapRecovery';
import { queryClient, recapKey, refreshRecap } from '@/lib/queryClient';
import { useCanonicalCollectionScope } from '@/features/feedback-integration/useCanonicalFeedback';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';
import type { RecapAcknowledgmentsResponse, RecapCommand, RecapDetail, RecapFullViewResponse,
  RecapOwnReadingResponse, RecapScope } from '@/lib/types';

export function useRecapScope(view: RecapScope['view']): RecapScope | null {
  // Reuse Q09's read-lifetime observer: even batched A→B→A returns a new
  // scope object, without reviving an old disclosure's permanent boundary.
  return useCanonicalCollectionScope(view);
}
export function useRecapBoundary(scope: RecapScope) {
  const boundary = useMemo(() => {
    const authority = feedbackConversationAuthorityFingerprint(); let departed = false;
    const current = () => {
      if (!isCurrentRecapScope(scope) || feedbackConversationAuthorityFingerprint() !== authority) departed = true;
      return !departed;
    };
    return { current, subscribe: (notify: () => void) => {
      const changed = () => { current(); notify(); };
      const auth = useAuthStore.subscribe(changed), selection = useScopeStore.subscribe(changed), token = observeAuthTokenChanges(changed);
      changed(); return () => { auth(); selection(); token(); };
    } };
    // The keyed owner captures this exact scope once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { ...boundary, active: useSyncExternalStore(boundary.subscribe, boundary.current, () => false) };
}
type Purpose = 'seen' | 'reading';
type ReaderCommand = Extract<RecapCommand, { action: 'seen' | 'own-reading' }>;
type Command = ReturnType<typeof captureRecapCommand<ReaderCommand>>;
type Action = { controller: Command | null; rowRevision: string | null; busy: boolean; error: string | null };
type State = { current: boolean; active: boolean; loading: boolean; denied: boolean; error: string | null;
  detail: RecapDetail | null; own: RecapOwnReadingResponse | null; full: RecapFullViewResponse | null;
  acknowledgments: RecapAcknowledgmentsResponse | null; seen: Action; reading: Action };
const emptyAction = (): Action => ({ controller: null, rowRevision: null, busy: false, error: null });

/** Mounted presentation owner. Q08 alone owns command bytes, idempotency and
 * receipts. This owner survives disclosure close; it has no persistence codec. */
export function createCanonicalRecapReader(scope: RecapScope, recapId: string) {
  const captured = { ...scope }, boundary = captureRecapBoundary(captured), listeners = new Set<() => void>();
  let state: State = { current: boundary.current(), active: false, loading: false, denied: false, error: null,
    detail: null, own: null, full: null, acknowledgments: null, seen: emptyAction(), reading: emptyAction() };
  let disposed = false, generation = 0, fullPending = false;
  let readAbort = new AbortController(); const commandAbort = new AbortController();
  const keys = new Map<string, ReturnType<typeof recapKey>>();
  const update = (patch: Partial<State>) => { state = { ...state, ...patch }; listeners.forEach(notify => notify()); };
  const action = (purpose: Purpose, patch: Partial<Action>) => update({ [purpose]: { ...state[purpose], ...patch } });
  const current = () => !disposed && boundary.current();
  const visible = () => current() && state.active && !state.denied;
  const cancelReads = () => {
    generation++; readAbort.abort(); fullPending = false;
    keys.forEach(queryKey => { void queryClient.cancelQueries({ queryKey, exact: true }, { revert: false }); });
  };
  const observe = () => {
    if (current()) return;
    cancelReads(); commandAbort.abort(); update({ current: false, active: false, loading: false, detail: null, full: null, own: null, acknowledgments: null });
  };
  const off = [useAuthStore.subscribe(observe), useScopeStore.subscribe(observe), observeAuthTokenChanges(observe)];
  function captureError(error: unknown) {
    if (isRecapContentUnavailable(error) || error instanceof ApiError && error.status === 401)
      update({ denied: true, detail: null, own: null, full: null, acknowledgments: null });
  }
  function read<T>(projection: 'detail' | 'own-reading' | 'acknowledgments', call: (signal: AbortSignal) => Promise<T>) {
    const key = recapKey(captured, recapId, projection); keys.set(projection, key);
    const turn = generation, signal = readAbort.signal;
    return queryClient.fetchQuery({ queryKey: key, staleTime: 0, retry: false, queryFn: async () => {
      try { return await call(signal); }
      catch (error) {
        // An authoritative API denial evicts/cancels the query before its
        // consumer sees the original error. Keep the denial on this owner.
        if (current() && turn === generation) captureError(error);
        throw error;
      }
    } });
  }
  async function load() {
    if (!visible()) return false;
    cancelReads(); const turn = generation; readAbort = new AbortController();
    update({ loading: true, error: null, detail: null, own: null, full: null, acknowledgments: null });
    try {
      const detail = await read('detail', signal => getRecap(recapId, { scope: captured, signal }));
      if (!visible() || turn !== generation) return false;
      if (detail.recap_id !== recapId || detail.view !== captured.view) throw Error('Recap selection changed');
      let own: RecapOwnReadingResponse | null = null, acknowledgments: RecapAcknowledgmentsResponse | null = null;
      if (captured.view === 'coach') acknowledgments = await read('acknowledgments', signal => getRecapAcknowledgments(recapId, { scope: captured, signal }));
      else if (detail.view === 'recipient' && detail.can_record_own_reading)
        own = await read('own-reading', signal => getRecapOwnReading(recapId, { scope: captured, signal }));
      if (!visible() || turn !== generation) return false;
      if ([own, acknowledgments].some(value => value && (value.recap_id !== recapId
        || value.revision_id !== detail.revision.id || value.version !== detail.revision.version))) throw Error('Recap changed while opening');
      update({ detail, own, acknowledgments });
      for (const purpose of ['seen', 'reading'] as const) {
        if (state[purpose].controller?.getState().status === 'accepted') {
          state[purpose].controller!.dispose(); update({ [purpose]: emptyAction() });
        }
      }
      return true;
    } catch (error) {
      if (visible() && turn === generation) { captureError(error); update({ error: 'This recap could not be confirmed. Refresh to read its current version.' }); }
      return false;
    } finally { if (current() && turn === generation) update({ loading: false }); }
  }
  async function run(purpose: Purpose) {
    const slot = state[purpose]; if (!current() || slot.busy || !slot.controller) return;
    action(purpose, { busy: true, error: null });
    try {
      await slot.controller.send(commandAbort.signal);
      if (!current()) return;
      await refreshRecap(captured, recapId);
      if (!current()) return;
      if (visible()) await load();
      else if (!state.active && slot.controller.getState().status === 'accepted') {
        // A row-only action has no open reader to hydrate. Its validated ACK
        // and completed collection refresh settle that action; a later row
        // click may intentionally select a newer revision.
        slot.controller.dispose(); update({ [purpose]: emptyAction() });
      }
    } catch (error) {
      if (current()) { captureError(error); action(purpose, { error: 'The original result is not confirmed. Your selection is kept.' }); }
    } finally { if (current()) action(purpose, { busy: false }); }
  }
  async function rowProof() {
    const revision = state.reading.rowRevision;
    if (!current() || !revision || state.reading.busy || state.reading.controller) return;
    action('reading', { busy: true, error: null });
    try {
      const proof = await prepareRecapOwnRead(recapId, { schema_version: 'recap-own-read-proof.v1', expected_published_revision_id: revision },
        { scope: captured, signal: commandAbort.signal });
      if (!current()) return;
      if (proof.recap_id !== recapId || proof.revision_id !== revision) throw Error('Recap proof selection changed');
      action('reading', { controller: captureRecapCommand(captured, { action: 'own-reading', recapId, body: {
        schema_version: 'recap-own-read.v1', operation_id: crypto.randomUUID(), expected_published_revision_id: revision,
        served_content_proof: proof.served_content_proof } }), rowRevision: null, busy: false });
      await run('reading');
    } catch (error) {
      if (current()) {
        captureError(error);
        // This endpoint only issues a proof. A definite rejection did not
        // capture or send an own-reading command, so a new explicit row
        // selection is safe. Uncertain proofs retain the selected revision.
        const rejected = error instanceof ApiError && error.status >= 400 && error.status < 500 && !state.reading.controller;
        action('reading', { busy: false, ...(rejected ? { rowRevision: null } : {}), error: rejected
          ? 'That recap version could not be selected. Choose its current row explicitly.'
          : 'Could not confirm reading for that selected recap version. Retry the original selection.' });
      }
    }
  }
  return {
    scope: captured, recapId,
    subscribe: (notify: () => void) => { listeners.add(notify); return () => { listeners.delete(notify); }; },
    getState: () => state,
    current,
    setActive(active: boolean) {
      if (!current() || state.active === active) return;
      if (!active) { cancelReads(); update({ active: false, loading: false, detail: null, full: null, own: null, acknowledgments: null }); }
      else { update({ active: true, denied: false }); void load(); }
    },
    refresh: () => { if (current()) { update({ denied: false }); return load(); } return Promise.resolve(false); },
    async prepareFullView() {
      const detail = state.detail;
      if (!visible() || !detail || !state.own || state.own.read_for_me || state.full || state.reading.controller
        || state.reading.rowRevision || state.reading.busy || state.error || fullPending) return;
      const turn = generation; fullPending = true;
      try {
        const full = await serveRecapFullView(recapId, { schema_version: 'recap-full-view.v1' }, { scope: captured, signal: readAbort.signal });
        if (!visible() || turn !== generation) return;
        if (full.recap_id !== recapId || full.revision.id !== detail.revision.id || full.revision.version !== detail.revision.version
          || full.scope_generation !== detail.scope_generation || full.own_read.revision_id !== full.revision.id) throw Error('Recap changed before full view');
        update({ full });
      } catch (error) {
        if (visible() && turn === generation) { captureError(error); update({ error: 'The full recap could not be confirmed. Refresh before reading.' }); }
      } finally { if (turn === generation) fullPending = false; }
    },
    recordVisibleReading(isVisible: () => boolean) {
      const full = state.full;
      if (!visible() || !full || state.error || state.reading.controller || state.reading.rowRevision || state.reading.busy
        || full.own_read.read_for_me || !isVisible()) return Promise.resolve();
      action('reading', { controller: captureRecapCommand(captured, { action: 'own-reading', recapId, body: {
        schema_version: 'recap-own-read.v1', operation_id: crypto.randomUUID(), expected_published_revision_id: full.revision.id,
        served_content_proof: full.served_content_proof } }) });
      return run('reading');
    },
    markReadFromRow(revisionId: string) {
      if (!current() || captured.view !== 'recipient' || state.reading.controller || state.reading.rowRevision || state.reading.busy) return;
      action('reading', { rowRevision: revisionId }); void rowProof();
    },
    setSeen(seen: boolean) {
      const detail = state.detail;
      if (!visible() || !detail || detail.view !== 'recipient' || !detail.can_change_seen || state.seen.controller || state.seen.busy) return;
      action('seen', { controller: captureRecapCommand(captured, { action: 'seen', recapId, body: {
        schema_version: 'recap-seen.v1', operation_id: crypto.randomUUID(), expected_published_revision_id: detail.revision.id, seen } }) });
      void run('seen');
    },
    retry(purpose: Purpose) { return purpose === 'reading' && state.reading.rowRevision ? rowProof() : run(purpose); },
    releaseRejected(purpose: Purpose) {
      if (!current() || state[purpose].busy || state[purpose].controller?.getState().status !== 'rejected') return;
      state[purpose].controller!.dispose(); update({ [purpose]: emptyAction() }); if (visible()) void load();
    },
    dispose() {
      if (disposed) return; cancelReads(); commandAbort.abort(); boundary.dispose(); off.forEach(stop => stop());
      state.seen.controller?.dispose(); state.reading.controller?.dispose(); disposed = true; listeners.clear();
    },
  };
}
export type CanonicalRecapReaderOwner = ReturnType<typeof createCanonicalRecapReader>;
export function useCanonicalRecap(owner: CanonicalRecapReaderOwner, active: boolean) {
  const state = useSyncExternalStore(owner.subscribe, owner.getState, owner.getState);
  useEffect(() => { owner.setActive(active); return () => owner.setActive(false); }, [owner, active]);
  return { ...state, detail: active && state.current && state.active && !state.loading && !state.denied ? state.detail : null };
}
